import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

// Recursive JSON renderer
type JsonNodeProps = {
  keyName?: string;
  value: any;
  searchTerm: string;
  currentMatchIndex: number;
  matchPaths: string[][];
  expandedPaths: Set<string>;
  setExpandedPaths: React.Dispatch<React.SetStateAction<Set<string>>>;
  path: string[];
  matchRefs: React.MutableRefObject<(HTMLSpanElement | null)[]>;
  globalMatchCounter: React.MutableRefObject<number>;
  level?: number;
};

const JsonNode: React.FC<JsonNodeProps> = ({
  keyName,
  value,
  searchTerm,
  currentMatchIndex,
  matchPaths,
  expandedPaths,
  setExpandedPaths,
  path,
  matchRefs,
  globalMatchCounter,
  level = 0,
}) => {
  const isObject = typeof value === "object" && value !== null;
  const isArray = Array.isArray(value);
  const pathStr = path.join(".");
  const isRoot = path.length === 1 && path[0] === "root";
  const isExpanded = isRoot ? true : expandedPaths.has(pathStr);

  // Helper to highlight and assign refs for each match
  const highlightText = (text: string): React.ReactNode => {
    if (!searchTerm.trim()) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      // push text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      const matchIdx = globalMatchCounter.current;
      const isCurrent = matchIdx === currentMatchIndex;
      globalMatchCounter.current += 1;

      parts.push(
        <span
          key={matchIdx}
          ref={(el) => {
            if (isCurrent) matchRefs.current[matchIdx] = el;
          }}
          className={`rounded px-0.5 ${
            isCurrent ? "bg-orange-400 text-black" : "bg-yellow-400 text-black"
          }`}
        >
          {match[0]}
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    // push remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const keysColor = "text-[#DF3079]";

  return (
    <div className={clsx("ml-4", level > 0 && "ml-16", "leading-4", "mb-2")}>
      {isObject || isArray ? (
        <div>
          {!isRoot && (
            <span
              onClick={() => {
                const newSet = new Set(expandedPaths);
                if (isExpanded) newSet.delete(pathStr);
                else newSet.add(pathStr);
                setExpandedPaths(newSet);
              }}
              className="cursor-pointer text-slate-400 select-none"
            >
              {isExpanded ? "▼" : "▶"}{" "}
            </span>
          )}
          {!isRoot && keyName && (
            <span className={keysColor}>{highlightText(keyName)}: </span>
          )}
          {!isRoot && (
            <span className="text-slate-400">
              {isArray ? `[${value.length}]` : `{${Object.keys(value).length}}`}
            </span>
          )}
          {isExpanded &&
            (isArray
              ? value.map((v, i) => (
                  <JsonNode
                    key={i}
                    keyName={`${i}`}
                    value={v}
                    searchTerm={searchTerm}
                    currentMatchIndex={currentMatchIndex}
                    matchPaths={matchPaths}
                    expandedPaths={expandedPaths}
                    setExpandedPaths={setExpandedPaths}
                    path={[...path, String(i)]}
                    matchRefs={matchRefs}
                    globalMatchCounter={globalMatchCounter}
                    level={level + 1}
                  />
                ))
              : Object.entries(value).map(([k, v]) => (
                  <JsonNode
                    key={k}
                    keyName={k}
                    value={v}
                    searchTerm={searchTerm}
                    currentMatchIndex={currentMatchIndex}
                    matchPaths={matchPaths}
                    expandedPaths={expandedPaths}
                    setExpandedPaths={setExpandedPaths}
                    path={[...path, k]}
                    matchRefs={matchRefs}
                    globalMatchCounter={globalMatchCounter}
                    level={level + 1}
                  />
                )))}
        </div>
      ) : (
        <div>
          {keyName && (
            <span className={keysColor}>{highlightText(keyName)}: </span>
          )}
          <span
            className={
              typeof value === "number"
                ? "text-blue-400"
                : typeof value === "boolean"
                ? "text-purple-400"
                : value === null
                ? "text-gray-400"
                : "text-[#00A67D]"
            }
          >
            {highlightText(String(value))}
          </span>
        </div>
      )}
    </div>
  );
};

type JsonViewerProps = {
  data: any;
  searchTerm: string;
  currentMatchIndex: number;
  onMatchesUpdate: (total: number, paths: string[][]) => void;
  scrollContainer: React.RefObject<HTMLDivElement>;
  matchPaths: string[][];
};

const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  searchTerm,
  currentMatchIndex,
  onMatchesUpdate,
  scrollContainer,
  matchPaths,
}) => {
  // expandedPaths: Set of stringified paths (e.g. "root.foo.bar.0")
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  // Track the global match index as we render
  const globalMatchCounter = useRef(0);
  const matchRefs = useRef<(HTMLSpanElement | null)[]>([]);

  // Add a ref to track if initial expansion has been done
  const hasInitializedExpansion = useRef(false);

  // Helper to stringify a path array
  const pathToString = (path: string[]) => path.join(".");

  // Helper to collect all paths in the JSON tree
  const collectAllPaths = useCallback(
    (node: any, path: string[] = ["root"], paths: Set<string> = new Set()) => {
      if (typeof node === "object" && node !== null) {
        // Check if this node is an event with name "data.input" or "data.output"
        if (node.name === "data.input" || node.name === "data.output") {
          // Expand all ancestors of this event
          for (let i = 1; i < path.length; i++) {
            const ancestorPath = pathToString(path.slice(0, i));
            paths.add(ancestorPath);
          }

          // Expand the current event path itself
          paths.add(pathToString(path));

          // Expand the attributes object inside this event
          if (node.attributes && typeof node.attributes === "object") {
            const attributesPath = pathToString([...path, "attributes"]);
            paths.add(attributesPath);

            // Recursively expand all nested objects/arrays in attributes
            const expandAllChildren = (n: any, p: string[]) => {
              if (typeof n === "object" && n !== null) {
                const currentPath = pathToString(p);
                paths.add(currentPath);

                if (Array.isArray(n)) {
                  n.forEach((v, i) => {
                    expandAllChildren(v, [...p, String(i)]);
                  });
                } else {
                  Object.entries(n).forEach(([k, v]) => {
                    expandAllChildren(v, [...p, k]);
                  });
                }
              }
            };

            expandAllChildren(node.attributes, [...path, "attributes"]);
          }
        }

        // Continue traversing the tree to find all data.input and data.output events
        if (Array.isArray(node)) {
          node.forEach((v, i) =>
            collectAllPaths(v, [...path, String(i)], paths)
          );
        } else {
          Object.entries(node).forEach(([k, v]) => {
            collectAllPaths(v, [...path, k], paths);
          });
        }
      }
      return paths;
    },
    []
  );

  // Find all matches and their paths
  const findMatches = useCallback(
    (node: any, path: string[] = ["root"], matches: string[][] = []) => {
      if (!searchTerm.trim()) return matches;
      const term = searchTerm.toLowerCase();

      if (typeof node === "object" && node !== null) {
        if (Array.isArray(node)) {
          node.forEach((v, i) => findMatches(v, [...path, String(i)], matches));
        } else {
          Object.entries(node).forEach(([k, v]) => {
            // count ALL occurrences in key
            const regex = new RegExp(
              term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
              "gi"
            );
            let m: RegExpExecArray | null;
            while ((m = regex.exec(k)) !== null) {
              matches.push([...path, k, "__key", String(m.index)]);
            }

            findMatches(v, [...path, k], matches);
          });
        }
      } else {
        // count ALL occurrences in value
        const valStr = String(node);
        const regex = new RegExp(
          term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
          "gi"
        );
        let m: RegExpExecArray | null;
        while ((m = regex.exec(valStr)) !== null) {
          matches.push([...path, "__value", String(m.index)]);
        }
      }

      return matches;
    },
    [searchTerm]
  );

  // Update matches and paths on search/data change
  useEffect(() => {
    const matches = findMatches(data);
    onMatchesUpdate(matches.length, matches);
  }, [data, searchTerm, findMatches, onMatchesUpdate]);

  // Only run when data changes
  useEffect(() => {
    if (data) {
      const allPaths = collectAllPaths(data);
      setExpandedPaths(allPaths);
      hasInitializedExpansion.current = true;
    }
  }, [data, collectAllPaths]);

  // Expand parents of matched items during search
  useEffect(() => {
    if (currentMatchIndex >= 0 && matchPaths && matchPaths[currentMatchIndex]) {
      const matchPath = matchPaths[currentMatchIndex];
      const parentPath = matchPath[matchPath.length - 1].startsWith("__")
        ? matchPath.slice(0, -1)
        : matchPath;

      setExpandedPaths((prev) => {
        const newExpanded = new Set(prev);
        for (let i = 1; i <= parentPath.length; i++) {
          newExpanded.add(pathToString(parentPath.slice(0, i)));
        }
        return newExpanded;
      });
    }
  }, [currentMatchIndex, matchPaths]);

  // Expand all parent paths of the current match
  useEffect(() => {
    if (currentMatchIndex >= 0 && matchPaths && matchPaths[currentMatchIndex]) {
      const matchPath = matchPaths[currentMatchIndex];
      // Remove __key/__value
      const parentPath = matchPath[matchPath.length - 1].startsWith("__")
        ? matchPath.slice(0, -1)
        : matchPath;
      // Expand all parent paths
      setExpandedPaths((prev) => {
        const newExpanded = new Set(prev);
        for (let i = 1; i <= parentPath.length; i++) {
          newExpanded.add(pathToString(parentPath.slice(0, i)));
        }
        return newExpanded;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatchIndex, matchPaths]);

  // Scroll to current match
  useEffect(() => {
    if (currentMatchIndex >= 0 && scrollContainer.current) {
      // Use requestAnimationFrame to wait for refs to be populated after render
      requestAnimationFrame(() => {
        const el = matchRefs.current[currentMatchIndex];
        const container = scrollContainer.current;
        if (el && container) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const containerScrollTop = container.scrollTop;
          const containerTop = containerRect.top;
          const elTop = elRect.top;
          const relativeElTop = elTop - containerTop + containerScrollTop;

          container.scrollTo({
            top: Math.max(0, relativeElTop - 20),
            behavior: "smooth",
          });
        }
      });
    }
  }, [
    currentMatchIndex,
    matchPaths,
    data.span_id,
    searchTerm,
    matchRefs.current.length,
  ]);

  // Reset matchRefs on each render
  matchRefs.current = [];
  globalMatchCounter.current = 0;

  return (
    <div className="h-full text-[13px] leading-[100%] font-normal text-slate-200">
      <div className="json-viewer">
        <JsonNode
          value={data}
          searchTerm={searchTerm}
          currentMatchIndex={currentMatchIndex}
          matchPaths={matchPaths}
          expandedPaths={expandedPaths}
          setExpandedPaths={setExpandedPaths}
          path={["root"]}
          matchRefs={matchRefs}
          globalMatchCounter={globalMatchCounter}
        />
      </div>
    </div>
  );
};
export default JsonViewer;
